import { useEffect } from 'react'
import { X } from 'lucide-react'
import { useSaveState } from '@/lib/save'

/** Long enough to read twice, short enough not to need dismissing. */
const LINGER_MS = 8000

/**
 * What the app says when a save did not take.
 *
 * One place, at the shell level, because the alternative — an error line under
 * every field — is thirty copies of the same component for something that
 * should almost never happen.
 *
 * Deliberately not a modal and not red-on-white alarm. The write failed; the
 * data is still there and you can try again. Interrupting the whole app for
 * that would be a bigger lie than saying nothing was.
 */
export function SaveNotice() {
  const message = useSaveState((state) => state.message)
  const dismiss = useSaveState((state) => state.dismiss)

  useEffect(() => {
    if (message === null) return
    const timer = setTimeout(dismiss, LINGER_MS)
    return () => clearTimeout(timer)
  }, [message, dismiss])

  if (message === null) return null

  return (
    <div
      role="alert"
      // Above the phone tab bar, out of the way of the thumb.
      className="pb-safe fixed inset-x-0 bottom-16 z-50 flex justify-center px-4 sm:bottom-4"
    >
      <div className="bg-foreground text-background flex max-w-md items-start gap-3 rounded-md px-3 py-2 text-sm shadow-lg">
        <span className="flex-1">{message}</span>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="opacity-70 transition-opacity hover:opacity-100"
        >
          <X className="size-4" aria-hidden />
        </button>
      </div>
    </div>
  )
}
